import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Facebook, Star, MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { contactInfo } from '../../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#2C2C2C] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/logo.png" 
                alt="InArch Design Studio" 
                className="h-14 w-auto object-contain"
              />
            </Link>
            <p className="text-[#9A9A9A] text-sm leading-relaxed mb-6 font-light">
              We don't just design spaces — we create environments that inspire, 
              connect, and enhance daily living.
            </p>
            <div className="divider-copper mb-6" />
            {/* Social Links */}
            <div className="flex items-center space-x-4">
              <a
                href={contactInfo.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 border border-[#4A4A4A] hover:border-[#B87333] hover:bg-[#B87333]/10 transition-all duration-300 group"
                aria-label="Instagram"
              >
                <Instagram size={18} className="text-[#9A9A9A] group-hover:text-[#B87333] transition-colors" />
              </a>
              <a
                href={contactInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 border border-[#4A4A4A] hover:border-[#B87333] hover:bg-[#B87333]/10 transition-all duration-300 group"
                aria-label="Facebook"
              >
                <Facebook size={18} className="text-[#9A9A9A] group-hover:text-[#B87333] transition-colors" />
              </a>
              <a
                href={contactInfo.social.google}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 border border-[#4A4A4A] hover:border-[#B87333] hover:bg-[#B87333]/10 transition-all duration-300 group"
                aria-label="Google Reviews"
              >
                <Star size={18} className="text-[#9A9A9A] group-hover:text-[#B87333] transition-colors" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-white">Navigation</h4>
            <ul className="space-y-3">
              {[
                { path: '/', label: 'Home' },
                { path: '/about', label: 'About Us' },
                { path: '/services', label: 'Services' },
                { path: '/gallery', label: 'Gallery' },
                { path: '/contact', label: 'Contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-[#9A9A9A] text-sm hover:text-[#B87333] transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight size={12} className="mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-[#B87333]" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-white">Services</h4>
            <ul className="space-y-3">
              {[
                'Residential Interiors',
                'Commercial Interiors',
                'Landscape',
                'Villas & Resorts',
                'Modular Kitchens',
                '3D Visualizations',
              ].map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-[#9A9A9A] text-sm hover:text-[#B87333] transition-colors duration-300 flex items-center group"
                  >
                    <ArrowRight size={12} className="mr-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-[#B87333]" />
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-lg mb-6 text-white">Get in Touch</h4>
            <div className="space-y-4">
              <div className="flex items-start">
                <MapPin size={18} className="text-[#B87333] mt-1 mr-3 flex-shrink-0" />
                <div className="text-[#9A9A9A] text-sm leading-relaxed">
                  <p>{contactInfo.address.line1}</p>
                  <p>{contactInfo.address.line2}</p>
                  <p>{contactInfo.address.line3}</p>
                  <p>{contactInfo.address.line4}</p>
                </div>
              </div>
              <div className="flex items-center">
                <Phone size={18} className="text-[#B87333] mr-3 flex-shrink-0" />
                <div className="flex flex-col">
                  <a
                    href={`tel:${contactInfo.phone}`}
                    className="text-[#9A9A9A] text-sm hover:text-[#B87333] transition-colors"
                  >
                    {contactInfo.phone}
                  </a>
                  <a
                    href={`tel:${contactInfo.phone2}`}
                    className="text-[#9A9A9A] text-sm hover:text-[#B87333] transition-colors"
                  >
                    {contactInfo.phone2}
                  </a>
                </div>
              </div>
              <div className="flex items-center">
                <Mail size={18} className="text-[#B87333] mr-3 flex-shrink-0" />
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-[#9A9A9A] text-sm hover:text-[#B87333] transition-colors"
                >
                  {contactInfo.email}
                </a>
              </div>
              <div className="flex items-center pt-2">
                <Instagram size={18} className="text-[#B87333] mr-3 flex-shrink-0" />
                <a
                  href={contactInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#9A9A9A] text-sm hover:text-[#B87333] transition-colors"
                >
                  Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#3A3A3A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-[#6B6B6B] text-xs tracking-wide">
              © {currentYear} InArch Design Studio. All rights reserved.
            </p>
            <p className="text-[#6B6B6B] text-xs tracking-wide">
              Crafting exceptional spaces since 2012
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
